import { useState, useEffect, useRef } from 'react';
import Checkbox from '#lib/components/checkbox.jsx';
import { isoDateTimeToString } from '#lib/utils/string.js';
import ImportButton from '#components/import-button.jsx';
import { makePaginatedTableColumns } from '#lib/helpers/table.jsx';
import { intlHeaderTexts } from '#lib/helpers/intlHeaders.js';

export default function useOrdersDeps(props) {
    const {
        selectedRows,
        isAllSelected,
        data,
        setSelectedRows,
        setIsAllSelected,
        importFns,
        importLoading,
        resetSelected,
        setImportFns
    } = props;
    const multiImportRef = useRef(null);
    const [isShown, setIsShown] = useState(false);
    const [page, setPage] = useState(0);
    const [maxDate, setMaxDate] = useState('');
    const [minDate, setMinDate] = useState('');

    const countLength = (array) => {
        const lengths = array.pages?.flatMap((page) => page.results.length);
        return lengths.reduce((sum, n) => sum + n, 0);
    };

    useEffect(() => {
        // se sono tutte selezionate
        if (!multiImportRef.current) return;
        if (isAllSelected || countLength(data) === selectedRows.length) {
            multiImportRef.current.indeterminate = false;
            return;
        }
        // se almeno una è selezionata (ma non tutte)
        if (selectedRows?.length > 0 && !isAllSelected && countLength(data) > selectedRows.length) {
            multiImportRef.current.indeterminate = true;
            return;
        }
        // se nessuna è selezionata
        multiImportRef.current.indeterminate = false;
        return;
    }, [selectedRows.length, isAllSelected]);

    const renderedData = { pages: [] };
    let columnProps;
    if (data) {
        data?.pages?.forEach((page) =>
            renderedData?.pages?.push({
                results: page?.results?.map((item) => {
                    return {
                        select: (
                            <Checkbox.Full
                                key={'check-' + item.number}
                                checked={selectedRows.find((el) => el === item.number) || isAllSelected}
                                onChange={(e) => {
                                    let updated = selectedRows.slice();
                                    if (!e.target.checked) {
                                        setIsAllSelected(false);
                                        updated.splice(selectedRows.indexOf(item.number), 1);
                                    } else {
                                        updated.push(item.number);
                                    }
                                    setSelectedRows(updated);
                                }}
                            />
                        ),
                        number: item.number,
                        date: isoDateTimeToString(item.date),
                        referenceName: item.referenceName,
                        action: (
                            <ImportButton
                                item={item}
                                number={item.number}
                                busy={importLoading && importFns.find((v) => v === item.number)}
                                imports={item.imports}
                                onClick={() => {
                                    resetSelected();
                                    setImportFns([item.number]);
                                    setIsShown(true);
                                }}
                            />
                        )
                    };
                })
            })
        );

        const { columns } = makePaginatedTableColumns(
            renderedData,
            {},
            {
                sortable: false,
                toDetail: false,
                headerText: (accessor) => {
                    if (accessor !== 'select') return intlHeaderTexts(_t.orders)(accessor);

                    return (
                        <input
                            type="checkbox"
                            ref={multiImportRef}
                            checked={isAllSelected}
                            onChange={(e) => {
                                setIsAllSelected(e.target.checked);
                                const updated = [];
                                if (e.target.checked) {
                                    data.pages.map((page) =>
                                        page.results.forEach((el) => {
                                            updated.push(el.number);
                                        })
                                    );
                                }
                                setSelectedRows(updated);
                            }}
                            className="row-span-2 h-4 w-4 translate-y-0.5 cursor-pointer rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                        />
                    );
                }
            }
        );
        const templateColumns = '3.5% auto auto auto 20%';
        columnProps = {
            columns: columns,
            templateColumns: templateColumns,
            numberOfColumns: columns.length
        };
    }

    return {
        maxDate,
        minDate,
        setMaxDate,
        setMinDate,
        isShown,
        setIsShown,
        page,
        setPage,
        multiImportRef,
        columnProps,
        renderedData
    };
}
